/* eslint-disable object-curly-newline,no-plusplus,implicit-arrow-linebreak,no-restricted-globals */
/* global React, G */
import { Grid, CircularProgress, Box } from '@mui/material'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'
import ErrorBoundary from 'ui/Error'
import useMemoRef from '@platform/react/hook/useMemoRef'
import {
  Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState, type RefObject,
} from 'react'
import { usePagination, useMessageAPI, useAttachmentAPI, useStyles } from '@platform/react/hook'
import useEventHandler from '@platform/react/hook/useEventHandler'
import useEventCallback from '@platform/react/hook/useEventCallback'
import { differenceInMinutes, isBefore, parseISO, startOfDay } from 'date-fns'
import ApplicationContext from '@platform/react/context/application'
import { arrayOf, getFirstItem, type PlatformEvent } from 'lib/util'
import Icon from 'ui/Element/Icon'
import MessageItem, {
  type Message,
  type MessageEvents,
  type MessageIcons,
  type MessageLabels,
  type MessageVariant,
  type UploadEntry,
} from 'ui/Element/Message/List/Template/Message'
import NoRequestItem, {
  type ChannelLabel,
  type RequesterContact,
} from 'ui/Element/Message/List/Template/NoRequest'
import DateItem from 'ui/Element/Message/List/Template/Date'
import SystemItem, { type SystemMessageType } from 'ui/Element/Message/List/Template/System'
import Text from 'ui/Element/Text'

const DEFAULT_ROW_HEIGHT = 100
const START_INDEX = 100000
const ANCHOR_FREEZE_DURATION = 2000
const FREEZE_CANCEL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown']

const componentMapping: Record<string, any> = {
  message: MessageItem,
  date: DateItem,
  system: SystemItem,
}

interface DateRow {
  /** discriminator marking a day-separator row */
  type: 'date'
  /** unique identifier for the row */
  key: string
  /** the parsed timestamp of the day */
  value: any
}

interface SystemRow {
  /** discriminator marking a system-message row */
  type: 'system'
  /** unique identifier for the row */
  key?: string
  /** the system message type to render */
  data: SystemMessageType
}

interface MessageRow {
  /** discriminator marking a user-message row */
  type: 'message'
  /** unique identifier for the row */
  key?: string
  /** the message to render */
  data: Message & { id?: string }
  /** whether the message is displayed as primary or secondary */
  variant: MessageVariant
  /** whether to render the message footer */
  showFooter: boolean
}

type Row = DateRow | SystemRow | MessageRow

export interface BaseMessageListEvents {
  /** obtains the messages to display */
  onOpen: (e: PlatformEvent<{ page: string | null }>) => any
  /** returns the available system message types */
  getSystemMessageTypes: (arg: null) => SystemMessageType[]
  /** fetches the attachments for the given messages */
  onAttachments?: (messages: Message[]) => Promise<any[]>
  /** an event handler executed when a message is clicked */
  onClick?: (event: PlatformEvent) => void
  /** an event handler executed when a message is deleted */
  onDelete?: (event: PlatformEvent) => void
  /** an event handler executed when a message changes */
  onChange?: (event: PlatformEvent) => void
}

export interface MessageListEvents extends BaseMessageListEvents {
  /** returns metadata about the list's request/ticket */
  onCreate?: (arg: null) => {
    type?: string
    request?: unknown
    requesterContact?: RequesterContact
    channelLabels?: ChannelLabel[]
  } | undefined
  /** an event handler executed when the list is focused */
  onFocus?: (arg: null) => void
}

export interface BaseMessageListProps {
  /** value to be set through an action or an event */
  value?: { sourceId?: string, userId?: string, fullHeight?: boolean }
  /** called when the list reaches / leaves the bottom */
  onAtBottom?: (atBottom: boolean) => void
  /** translation of english 'me' */
  meLabel?: string
  /** text to be shown while a message is being sent */
  sendingLabel?: string
  /** text to show when the request has not been published */
  noRequestLabel?: string
  /** text shown while older messages are loading */
  loadingOlderMessagesLabel?: string
  /** text to show when a message has been deleted */
  deletedLabel?: string
  /** text to show when a message has been edited */
  editedLabel?: string
  /** text to show for an empty message */
  emptyLabel?: string
  /** delete icon */
  deleteIcon?: string
  /** edit icon */
  editIcon?: string
  /** the name of a group of attachments for the messages in the list */
  attachmentsGroup?: string
  /** number of items to render outside the viewport */
  overscanItemCount?: number
  /** child elements */
  children?: any
}

export interface MessageListProps extends BaseMessageListProps {
  /** a reference to the root element */
  ref?: RefObject<VirtuosoHandle>
  /** object containing the component's event handlers */
  events: MessageListEvents
  /** information about requester's contactChannels */
  contactChannels: { prefix?: Record<string, string>, decorators?: unknown }
}

/**
 * Returns the MessageList element's current theme styles.
 *
 * @param theme  the application's current theme
 * @returns      the MessageList element's styles
 */
export const styles = (theme: any) => ({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  root: {
    height: '100%',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  },
  alertBox: {
    position: 'absolute',
    borderRadius: 0,
    top: 0,
    right: 0,
    left: 0,
    zIndex: 1,
  },
  ...theme.custom.messageList,
})

/**
 * Builds the virtuoso row descriptors for the message list.
 *
 * Descriptor shapes:
 *  - `{ type: 'date', key, value }`                        a day separator
 *  - `{ type: 'system', key, data }`                       a system message
 *  - `{ type: 'message', key, data, variant, showFooter }` a user message
 *
 * @param p
 * @param p.data                the current list of messages (oldest first)
 * @param p.sourceId            the current source set for the list
 * @param p.systemMessageTypes  list of available system message types
 * @returns                     the row descriptors
 * @private
 */
const _children = ({ data, sourceId, systemMessageTypes }: {
  data: Message[]
  sourceId?: string
  systemMessageTypes: SystemMessageType[]
}): Row[] => {
  const timestamps: any[] = data.map(
    m => m.value?.submitTimestamp && parseISO(m.value.submitTimestamp),
  )

  return data.reduce<Row[]>((rows, current, index) => {
    const firstItem = index === 0
    const lastItem = index === data.length - 1
    // current message's parsed timestamp and refs
    const currentTimestamp = timestamps[index]
    const currentSourceKey = current.refs?.source?.[0]
    const currentSubmitterKey = getFirstItem(current.refs?.submitter)?.key || null
    // gathering previous message's relevant values
    const previous = data[index - 1]
    const previousTimestamp = timestamps[index - 1]
    const previousSourceKey = previous?.refs?.source?.[0]
    // const previousSubmitterKey = previous?.refs.submitter?.[0].key
    // gathering next message's relevant values
    const next = data[index + 1]
    const nextTimestamp = timestamps[index + 1]
    const nextSubmitterKey = getFirstItem(next?.refs?.submitter)?.key || null
    // checking messages' parameters
    const currentStartOfDay = startOfDay(currentTimestamp)
    const dayChanged = previousTimestamp ? isBefore(previousTimestamp, currentStartOfDay) : true
    const timeExceeded = nextTimestamp && differenceInMinutes(nextTimestamp, currentTimestamp) > 10
    const sameSource = previousSourceKey === currentSourceKey
    const sameSubmitter = nextSubmitterKey === currentSubmitterKey
    // setting flags
    const showFooter = lastItem || timeExceeded || !sameSource || !sameSubmitter
    const showDate = firstItem || dayChanged
    const systemMessage = systemMessageTypes.find(
      systemMessageType => systemMessageType.key === current.value.type,
    )
    const variant: MessageVariant = !currentSourceKey || sourceId === currentSourceKey ? 'primary' : 'secondary'

    // date separator descriptor
    if (showDate) {
      rows.push({ type: 'date', key: `date:${currentTimestamp}`, value: currentTimestamp })
    }
    // message / system descriptor
    rows.push(systemMessage
      ? { type: 'system', key: current.key, data: systemMessage }
      : {
        type: 'message',
        key: current.key,
        data: { id: current.key, ...current },
        variant,
        showFooter: !!showFooter,
      })
    return rows
  }, [])
}

/**
 * Adds the attachments from the listed messages to the `groupName` attachment group.
 *
 * TODO: Extend Attachment API so that we don't have to do this.
 *
 * @param state      the state of the list
 * @param groupName  the name of the group to attach the attachments to
 * @param handler    function for fetching message attachments
 */
export const attachToGroup = (
  state: { messages: Message[] },
  groupName?: string,
  handler: ((messages: Message[]) => Promise<any[]>) | boolean = false,
) => {
  const handlerRef = useRef(false)
  const { eventBus } = useContext(ApplicationContext) as any
  const [attachments, setAttachments] = useState<any[]>([])
  const { messages } = state

  useEffect(() => {
    if (!groupName) return
    // listening for the attachment API to set a new list of images to the attachment group
    eventBus.add(eventBus.type(G.ATTACHMENT, G.DONE, groupName), (event: any) => {
      const newAttachments = event.detail[G.DATA]
      const changed = newAttachments.filter((newAttachment: any) => !attachments.some(attachment =>
        attachment.key === newAttachment.key))
      setAttachments(changed.length ? newAttachments : [])
    }, { once: true })
    // forcing the attachment API to fire its current list of images from groupName (by sending an
    // empty G.DATA object), so that the listener above gets the attachments
    eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.CACHE, groupName), { [G.DATA]: {} })
  }, [])

  useEffect(() => {
    if (groupName && messages?.length) {
      /**
       * We only want to do this once after the messages
       * are available, therefore we use a reference.
       */
      !handlerRef.current
        && handler
        && (handler as (m: Message[]) => Promise<any[]>)(messages).then((fetchedAttachments) => {
          handlerRef.current = true

          // Copying the acl information from the fetched attachments to the attachments prop
          // of their message, so that when we render a message, we know if the user has
          // permission to delete its attachments
          for (let i = 0; i < messages.length; i++) {
            const message = messages[i]
            const messageAtts = message?.value?.attachments || []

            for (let y = 0; y < messageAtts.length; y++) {
              const messageAtt: any = messageAtts[y]
              const fetchedMessageAtt = fetchedAttachments.find(x => x.key === messageAtt.key)
              messageAtt.acl = fetchedMessageAtt.acl
            }
          }

          /**
           * On initial load, the GET to ticket/<id>/listing/attachment returns ticket-
           * as well as message attachments. Because we may already have message
           * attachments, we need to replace them with the ones we enriched
           * with the additional data.
           *
           * Simply calling setAttachments(fetchedAttachments)
           * would override the ticket attachments!
           */
          setAttachments((prevState) => {
            /**
             * First our attachments, then the prevState so that
             * `findIndex` below finds our attachments
             * first.
             */
            const concatenatedState = fetchedAttachments.concat(prevState)
            return concatenatedState.filter(
              (m, i) => i === concatenatedState.findIndex(n => n.key === m.key),
            )
          })
        })

      // Everytime we do this, we need to ensure that we first remove all the message and note
      // attachments from the list, because we may have deleted a message attachment and therefore
      // don't want to show it anymore.
      const parentAttachments = attachments.filter(attachment => attachment.value.parentType !== 'message' && attachment.value.parentType !== 'note')

      // concatenating current group attachments with those from the messages
      const data = parentAttachments?.concat(
        messages
          // accepting only messages that have attachments
          .filter(message => message.value.attachments?.length)
          // mapping each message item to its attachments
          .map(message => message.value.attachments)
          // converting the resulting array of arrays to a single-level array
          .flat()
          // accepting only attachments not already contained by the attachments state
          .filter((messageAttachment: any) => !parentAttachments.some(attachment =>
            messageAttachment.key === attachment.key))
          // setting new = true for attachments that don't have a submitTimestamp
          // e.g. new attachments
          .map((message: any) =>
            (!message.value?.submitTimestamp && { ...message, newAttachment: true }) || message),
      )

      // dispatching the new list attachments (if any)
      data.length && eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.CACHE, groupName), {
        [G.DATA]: {
          [G.DATA]: data,
        },
      })
    }
  }, [state, attachments])
}

/**
 * MessageList component.
 *
 * Listens to messages, processes them and displays them as a list.
 *
 * @param props                    the element's properties
 * @param props.ref                a reference to the root element
 * @param props.value              value to be set through an action or an event
 * @param props.onAtBottom         called when the list reaches / leaves the bottom
 * @returns                        the new MessageList element
 */
const MessageList = ({ ref, value, onAtBottom, ...props }: MessageListProps) => {
  const {
    meLabel,
    sendingLabel,
    noRequestLabel,
    loadingOlderMessagesLabel,
    deletedLabel,
    editedLabel,
    emptyLabel,
    events,
    deleteIcon,
    editIcon,
    attachmentsGroup,
    overscanItemCount = 10,
  } = props

  const {
    onCreate,
    onOpen,
    getSystemMessageTypes,
    onAttachments,
    onClick,
    onDelete,
    onChange,
  } = events

  const { sourceId, userId } = value || {}
  const classes = useStyles(styles)() as Record<string, string>
  const { eventBus } = useContext(ApplicationContext) as any

  const systemMessageTypes = getSystemMessageTypes(null)
  const [messages, pending] = useMessageAPI() as [Message[], boolean]
  const uploads = useAttachmentAPI().uploads as UploadEntry[]

  // { key: messageId, value: [attachments] }
  const messageUploads = useMemo(
    () => uploads.reduce<Record<string, UploadEntry[]>>((acc, entry) => {
      if (!acc[entry.parent]) acc[entry.parent] = []
      acc[entry.parent].push(entry)
      return acc
    }, {}),
    [uploads],
  )
  const [currentPage, setCurrentPage] = useState<string | null>(null)
  const [firstItemIndex, setFirstItemIndex] = useState(START_INDEX)
  const isFetchingRef = useRef(false)

  const rowsRef = useRef<Row[]>([])
  const scrollerRef = useRef<HTMLElement | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)
  const anchorFreezeRef = useRef<{ node: HTMLElement, offset: number } | null>(null)
  const freezeTimerRef = useRef<any>(0)

  // Setting the anchor to the top-most visible element
  const captureAnchor = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const scrollerTop = el.getBoundingClientRect().top
    const currentRows = rowsRef.current
    let anchor: { node: HTMLElement, top: number } | null = null

    // for each rendered item
    el.querySelectorAll<HTMLElement>('[data-index]').forEach((node) => {
      const item = currentRows[Number(node.dataset.index)]
      if (!item || item.type === 'date') return
      const rect = node.getBoundingClientRect()

      // is in viewport (rect.bottom > scrollerTop) AND anchor not set or item lower than anchor
      if (rect.bottom > scrollerTop && (!anchor || rect.top < anchor.top)) {
        anchor = { node, top: rect.top }
      }
    })
    // `anchor` is captured and assigned inside the forEach closure, which
    // defeats TS control-flow narrowing — re-widen it through a typed alias.
    const held = anchor as { node: HTMLElement, top: number } | null
    anchorFreezeRef.current = held && {
      node: held.node,
      offset: held.top - scrollerTop,
    }

    clearTimeout(freezeTimerRef.current)
    freezeTimerRef.current = setTimeout(
      () => { anchorFreezeRef.current = null },
      ANCHOR_FREEZE_DURATION,
    )
  }, [])

  // Freezes the row holding the anchor (and therefore the whole list)
  // in place while a new chunk comes in and settles (image display etc)
  const freezeAnchor = useCallback(() => {
    const hold = anchorFreezeRef.current
    const el = scrollerRef.current
    if (!hold || !el) return

    if (!hold.node.isConnected) {
      anchorFreezeRef.current = null
      return
    }

    const drift = hold.node.getBoundingClientRect().top
      - el.getBoundingClientRect().top
      - hold.offset
    if (drift) el.scrollTop += drift
  }, [])

  // Creates an observer that will observe all list items
  const handleScrollerRef = useCallback((el: any) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    scrollerRef.current = el
    anchorFreezeRef.current = null
    if (!el) return

    FREEZE_CANCEL_EVENTS.forEach(type => el.addEventListener(
      type,
      () => { anchorFreezeRef.current = null },
      { passive: true },
    ))

    const ro = new ResizeObserver(freezeAnchor)
    el.querySelectorAll('[data-testid="virtuoso-item-list"]').forEach((node: Element) => ro.observe(node))
    observerRef.current = ro
  }, [])

  const { data: pageData, page, pending: fetching, loadNext } = usePagination(
    onOpen,
    null,
    null,
    currentPage,
    setCurrentPage,
    [],
  )

  useEffect(() => {
    if (fetching) return

    const isFirstPage = currentPage === null

    if (!isFirstPage && pageData.length > 0) {
      captureAnchor()
      // NOTE: `firstItemIndex` starts with `START_INDEX` and gradually decreases with each
      // new chunk. This means that we can display max `START_INDEX` messages in the chat:
      // https://virtuoso.dev/react-virtuoso/api-reference/virtuoso/#firstitemindex-number
      setFirstItemIndex(index => index - pageData.length)
    }

    eventBus.dispatch(
      eventBus.type(G.MESSAGE, G.DATA),
      { [G.DATA]: pageData, [G.STATE]: { insert: !isFirstPage } },
    )
    isFetchingRef.current = false
  }, [pageData, fetching])

  const handleNext = useCallback(() => {
    if (isFetchingRef.current || fetching || !page) return
    isFetchingRef.current = true
    loadNext()
  }, [fetching, page, loadNext])

  const handleNewMessage = useEventCallback(() => {
    ref?.current?.scrollToIndex({ index: 'LAST', align: 'end', behavior: 'smooth' })
  })
  useEventHandler(eventBus, eventBus.type(G.MESSAGE, G.SCROLL), handleNewMessage)

  const remove = useMemo(() => (
    <Icon
      source={deleteIcon}
      size={'1.5rem'}
      iconPadding={'0.5rem'}
      shape={'circle'}
    />
  ), [deleteIcon])

  const edit = useMemo(() => (
    <Icon
      source={editIcon}
      size={'1.5rem'}
      iconPadding={'0.5rem'}
      shape={'circle'}
    />
  ), [editIcon])

  const LoadingIndicator = useMemo(() =>
    ({ context }: { context?: { fetching?: boolean, pending?: boolean } }) =>
      (context?.fetching || context?.pending
        ? (
          <Grid
            container
            gap={'0.25rem'}
            padding={'1rem'}
            alignItems={'center'}
            justifyContent={'center'}
            sx={{
              top: 0,
              left: 0,
              right: 0,
              position: 'absolute',
              backgroundColor: (t: any) => t.palette.white.main,
            }}
          >
            <CircularProgress
              size={'0.75rem'}
              sx={{ color: (theme: any) => theme.palette.gray[500] }}
            />
            <Text
              variant={'14/medium'}
              color={'gray.500'}
              value={loadingOlderMessagesLabel}
            />
          </Grid>
        ) : null),
  [])

  const icons = useMemo<MessageIcons>(() => ({ remove, edit }), [remove, edit])
  const messageEvents = useMemo<MessageEvents>(
    () => ({ onClick, onDelete, onChange }),
    [onClick, onDelete, onChange],
  )
  const messageLabels = useMemo<MessageLabels>(
    () => ({ meLabel, sendingLabel, deletedLabel, editedLabel, emptyLabel }),
    [meLabel, sendingLabel, deletedLabel, editedLabel, emptyLabel],
  )

  const renderItem = useCallback((index: number, item: any) => {
    const Component = componentMapping[item.type]

    return (
      <Box
        key={item.key || index}
        sx={{
          paddingTop: index === firstItemIndex
            ? '0.5rem'
            : 0,
        }}
      >
        <Component
          {...item}
          key={item.key || index}
          id={item.id || index}
          message={item.data}
          icons={icons}

          events={messageEvents}
          labels={messageLabels}

          currentUserId={userId}
          attachmentsGroup={attachmentsGroup}
          uploads={messageUploads[item.key]}
        />
      </Box>
    )
  }, [
    messageEvents, icons, messageLabels, userId,
    attachmentsGroup, firstItemIndex, messageUploads,
  ])

  const { type, request, requesterContact, channelLabels } = onCreate?.(null) || {}

  // No useMemo here
  const rows = messages.length
    ? _children({ data: messages, sourceId, systemMessageTypes })
    : []

  rowsRef.current = rows

  const hasMessages = rows.length > 0

  const fallback = hasMessages
    ? null
    : (type === 'ticket' && !request // ticket with no request ref -> not published
      && <NoRequestItem
        label={noRequestLabel}
        content={requesterContact}
        channelLabels={channelLabels}
        prefixes={props.contactChannels.prefix}
        decorators={props.contactChannels.decorators}
      />
    ) || arrayOf(props?.children || []).find((c: any) => c.key === 'nodata')

  attachToGroup({ messages }, props.attachmentsGroup, onAttachments)

  const listComponents = {
    Header: LoadingIndicator,
  }

  return (
    <ErrorBoundary>
      <Grid
        container
        item
        direction={'column'}
        wrap={'nowrap'}
        className={classes.root}
      >
        {hasMessages ? (
          <Virtuoso
            ref={ref}
            scrollerRef={handleScrollerRef}
            data={rows}
            firstItemIndex={firstItemIndex}
            computeItemKey={(index, item) => item?.key ?? index}
            itemContent={renderItem}

            followOutput={'smooth'}

            startReached={handleNext}
            atBottomStateChange={onAtBottom}
            // logLevel={LogLevel.DEBUG}

            components={listComponents}
            context={{ fetching, pending }}

            style={{ height: '100%' }}
            initialTopMostItemIndex={Math.max(rows.length - 1, 0)}
            increaseViewportBy={{
              top: DEFAULT_ROW_HEIGHT * (overscanItemCount || 5),
              bottom: DEFAULT_ROW_HEIGHT * (overscanItemCount || 5),
            }}
            minOverscanItemCount={{
              top: overscanItemCount,
              bottom: overscanItemCount,
            }}
          />
        ) : !pending && (
          <Suspense fallback={<></>}>
            {fallback}
          </Suspense>
        )}
      </Grid>
    </ErrorBoundary>
  )
}

export default useMemoRef(MessageList, (props: MessageListProps) => [
  props.children,
  props.value?.sourceId,
  props.value?.userId,
])
